import { Fragment, type ReactNode } from 'react'
import { HighlightMatch } from '@/shared/components/ui'
import { formatDate, useDateFormat } from '@/shared/utils/format-date'
import type { GlobalSearchResult } from './use-global-search'
import type { IdField } from './use-chart-id-search'
import { isShown, type IdHit } from '../utils/id-hit'
import { MatchedId } from '../components/matched-id'
import { statusDisplay } from '../utils/status-groups'

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
// A masked DOS (asterisks, then the year): only its visible year, or null when nothing is visible.
const maskedDosYear = (raw: string) => /\d{4}/.exec(raw)?.[0] ?? null

/** Results carry `encounter-<id>` (quick search's flattened id). */
export const chartRoute = (r: GlobalSearchResult) => `/workspace/queue/${r.id.replace(/^encounter-/, '')}`

/** An identifier for an accessible name: its value, "masked" or "missing" — never asterisks. */
const spoken = (v: string | null | undefined) => (isShown(v) ? v : v ? 'masked' : 'missing')

/**
 * One compact chart row's content, then the status pill. Single line (the meta
 * ellipsizes).
 *
 * Every role gets `med_id` (the platform tracking id, not PHI). Which identifier
 * leads depends on whether the claim id is visible:
 * - Claim ID masked ("*****") or absent (the internal population): every row shows
 *   all three identifiers in one fixed order — Med ID (the primary), then
 *   "Claim ID …" and "Patient ID …" in the meta — whichever one matched. A value
 *   they may not see shows exactly as returned ("*****"), never left out.
 * - Claim ID visible (SaaS users): Claim ID stays primary; Med ID and Patient ID
 *   go in the meta (a masked Patient ID would be left out).
 *
 * Then facility · DOS (just its year when masked) · provider. The provider is
 * first and highlighted when the query hits it (how a name query like "Ferreira"
 * matched). The match is marked on the identifier that matched; a masked value is
 * never marked. `label` is the link's accessible name when the visible text would
 * not tell rows apart or would read asterisks (masked or missing identifiers).
 */
export function useChartRow() {
  const dateFmt = useDateFormat()
  return (r: GlobalSearchResult, query = '', hit?: IdHit) => {
    const m = r.metadata ?? {}
    const masked = !!m.date_of_service?.includes('*')
    // Masked: the year the user may see, never the asterisks.
    const dos = masked ? maskedDosYear(m.date_of_service!) : m.date_of_service ? formatDate(m.date_of_service, dateFmt) : null
    // Med ID leads only when the claim id is masked or absent; a real claim id stays primary.
    const internal = m.med_id != null && !isShown(m.claim_id)
    const provider = m.provider_name?.trim() || null
    const term = query.trim().toLowerCase()
    // Bulk rows match by identifier only: the provider is never "why" there.
    const providerHit = !hit && !!provider && term.length >= 2 && provider.toLowerCase().includes(term)
    // Mark the Med ID only the way the backend matches it: exact, or a prefix past the 'med_' stem.
    const med = m.med_id?.toLowerCase() ?? ''
    const medQuery = term.length >= 2 && (med === term || (!'med_'.startsWith(term) && med.startsWith(term))) ? query : ''
    const st = m.status ? statusDisplay(m.status, m.coding_mode ?? null) : null
    const id = (field: IdField, v: string | null | undefined, q = query) =>
      <MatchedId text={v || '--'} field={field} query={q} hit={hit} />
    const tail = [
      dos ? `DOS ${dos}` : masked ? 'DOS masked' : null,
      m.facility_name || 'no facility',
      provider && `provider ${provider}`,
      st?.label,
    ]
    // Internal rows always have a masked or missing claim id, so they always need a spoken label.
    const label = internal
      ? [`Open chart, Med ID ${m.med_id}`, `claim ID ${spoken(m.claim_id)}`, `patient ID ${spoken(m.patient_id)}`, ...tail].filter(Boolean).join(', ')
      : isShown(m.claim_id)
        ? undefined
        : [`Open chart, claim ID ${spoken(m.claim_id)}`, ...tail].filter(Boolean).join(', ')
    const content: ReactNode = (
      <>
        <span className="sp-row-primary">
          {internal ? <><span className="sp-row-label">Med ID </span>{id('med_id', m.med_id, medQuery)}</> : id('claim_id', m.claim_id)}
        </span>
        <span className="sp-row-meta">
          {[
            ...(internal
              ? [<>Claim ID {id('claim_id', m.claim_id)}</>, <>Patient ID {id('patient_id', m.patient_id)}</>, providerHit && provider && <HighlightMatch text={provider} query={query} />]
              : [providerHit && provider && <HighlightMatch text={provider} query={query} />, m.med_id && <>Med ID {id('med_id', m.med_id, medQuery)}</>, isShown(m.patient_id) && <>Patient ID {id('patient_id', m.patient_id)}</>]),
            m.facility_name && (hit ? m.facility_name : <HighlightMatch text={m.facility_name} query={query} />),
            dos,
            !providerHit && provider,
          ].filter(Boolean).map((v, i) => (
            <Fragment key={i}>
              {i > 0 && ' · '}
              {v}
            </Fragment>
          ))}
        </span>
        {st && <span className={`sp-row-status ${st.badgeClass}`}>{st.label}</span>}
      </>
    )
    return { content, label }
  }
}
